import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ind4_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 alone never ends the session. When one arrives we re-verify the
// token against /auth/me (with retries spaced out over several seconds) and
// only log out if that check itself keeps answering 401. Transient gateway /
// Cloudflare / backend-restart failures therefore never kick the user out.
const SILENT_401_PATHS = ['/notifications/unread-count', '/notifications'];
const VERIFY_DELAYS_MS = [0, 2500, 6000];
let verifyPromise = null;

function isSilentPath(url) {
  return !!url && SILENT_401_PATHS.some(p => url.includes(p));
}

function hardLogout() {
  localStorage.removeItem('ind4_token');
  localStorage.removeItem('ind4_user');
  if (window.location.pathname !== '/') {
    window.location.replace('/');
  }
}

export function verifySession() {
  if (verifyPromise) return verifyPromise;
  verifyPromise = (async () => {
    for (let i = 0; i < VERIFY_DELAYS_MS.length; i += 1) {
      if (VERIFY_DELAYS_MS[i]) await new Promise(r => setTimeout(r, VERIFY_DELAYS_MS[i]));
      const token = localStorage.getItem('ind4_token');
      if (!token) return false;
      try {
        await axios.get(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
        return true;
      } catch (err) {
        if (err.response?.status !== 401) return true; // transient → keep session
      }
    }
    return false;
  })().finally(() => { verifyPromise = null; });
  return verifyPromise;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !isSilentPath(url) && !url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/me')) {
      verifySession().then(ok => { if (!ok) hardLogout(); });
    }
    return Promise.reject(error);
  }
);

export default api;
